import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CRON_PRESETS,
  fetchSyncRuns,
  fetchSyncSchedule,
  fetchSyncStatus,
  formatDuration,
  formatRelative,
  triggerSync,
  updateSchedule,
  type SyncScheduleRow,
  type SyncStatusRow,
} from "@/lib/metadataSync";
import { AlertTriangle, CheckCircle2, Clock, History, Play, RefreshCw } from "lucide-react";

/**
 * Settings → "Metadata Sync" section. Shows one card per source, each
 * with live status + cron + Sync Now + schedule editor, plus a run-history
 * drawer at the bottom.
 *
 * Hidden entirely when the backend returns 404 for /metadata-sync/status —
 * that means the lens feature is disabled on this deployment, and we
 * shouldn't clutter the Settings page with a non-functional tab.
 */
export function MetadataSyncSection() {
  const qc = useQueryClient();

  // Poll faster when something's running so the "Sync Now" UX feels live.
  const status = useQuery({
    queryKey: ["metadata-sync", "status"],
    queryFn: fetchSyncStatus,
    refetchInterval: (q) => {
      const data = q.state.data as { sources: SyncStatusRow[] } | undefined;
      const anyRunning = data?.sources?.some((s) => s.lastStatus === "running");
      return anyRunning ? 3_000 : 30_000;
    },
    retry: (count, err) => {
      const anyErr = err as { response?: { status?: number } };
      if (anyErr?.response?.status === 404) return false;   // lens disabled
      return count < 2;
    },
  });

  const schedule = useQuery({
    queryKey: ["metadata-sync", "schedule"],
    queryFn: fetchSyncSchedule,
    enabled: !!status.data,
  });

  const [historyOpen, setHistoryOpen] = useState(false);

  // Hooks must run unconditionally per the Rules of Hooks. Compute
  // derived state FIRST, check the early-return flag AFTER — swapping
  // these ordered the hook count to change between renders (before
  // useMemo ran vs. after), tripping the minified "React #300 / #310"
  // hook-order error in the console.
  const sources = status.data?.sources ?? [];
  const schedMap = useMemo(() => {
    const m = new Map<string, SyncScheduleRow>();
    for (const s of schedule.data?.schedules ?? []) m.set(s.sourceName, s);
    return m;
  }, [schedule.data]);

  // Hide entirely if the backend says lens is off (/metadata-sync/status
  // only mounts when datumcraft.metadata.lens.enabled=true; 404 is the
  // "feature disabled" signal).
  if (status.isError && (status.error as { response?: { status?: number } })?.response?.status === 404) {
    return null;
  }

  return (
    <section className="rounded border border-datumcraft-slate/20 bg-white p-4">
      <header className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold">Metadata sync (lens)</h2>
        <button
          onClick={() => qc.invalidateQueries({ queryKey: ["metadata-sync"] })}
          className="inline-flex items-center gap-1 rounded border border-datumcraft-slate/30 px-2 py-1 text-xs hover:border-datumcraft-accentDark"
          aria-label="Refresh"
          title="Refresh"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </header>

      {status.isLoading ? (
        <p className="text-sm text-datumcraft-slate">Loading…</p>
      ) : sources.length === 0 ? (
        <p className="text-sm text-datumcraft-slate">No sources configured.</p>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {sources.map((src) => (
            <SourceCard
              key={src.sourceName}
              status={src}
              schedule={schedMap.get(src.sourceName) ?? null}
            />
          ))}
        </div>
      )}

      <div className="mt-4 border-t border-datumcraft-slate/15 pt-3">
        <button
          onClick={() => setHistoryOpen((v) => !v)}
          className="inline-flex items-center gap-1 text-xs font-medium text-datumcraft-ink hover:text-datumcraft-accentDark"
        >
          <History size={12} /> {historyOpen ? "Hide" : "Show"} run history
        </button>
        {historyOpen && <RunHistoryDrawer />}
      </div>
    </section>
  );
}

function SourceCard({
  status,
  schedule,
}: {
  status: SyncStatusRow;
  schedule: SyncScheduleRow | null;
}) {
  const qc = useQueryClient();
  const [customCron, setCustomCron] = useState<string>("");
  const [editing, setEditing] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  useEffect(() => {
    setCustomCron(schedule?.cronExpression ?? "");
  }, [schedule?.cronExpression]);

  const trigger = useMutation({
    mutationFn: () => triggerSync(status.sourceName),
    onSuccess: () => {
      setFeedback("Dispatched.");
      qc.invalidateQueries({ queryKey: ["metadata-sync"] });
    },
    onError: (e: unknown) => {
      const msg =
        (e as { response?: { data?: { message?: string } } }).response?.data?.message ??
        String(e);
      setFeedback(`Trigger failed: ${msg}`);
    },
  });

  const save = useMutation({
    mutationFn: (body: { cronExpression?: string; presetLabel?: string; enabled?: boolean }) =>
      updateSchedule(status.sourceName, body),
    onSuccess: () => {
      setEditing(false);
      setFeedback("Schedule saved.");
      qc.invalidateQueries({ queryKey: ["metadata-sync"] });
    },
    onError: (e: unknown) => {
      const msg =
        (e as { response?: { data?: { message?: string } } }).response?.data?.message ??
        String(e);
      setFeedback(`Save failed: ${msg}`);
    },
  });

  const running = status.lastStatus === "running";

  return (
    <article className="rounded-lg border border-datumcraft-slate/20 bg-datumcraft-mist/30 p-3">
      <header className="flex items-start justify-between">
        <div>
          <h3 className="text-sm font-semibold text-datumcraft-navy">{status.sourceName}</h3>
          <StatusBadge kind={status.lastStatus} />
        </div>
        <button
          disabled={running || trigger.isPending}
          onClick={() => { setFeedback(null); trigger.mutate(); }}
          className="inline-flex items-center gap-1 rounded-md bg-datumcraft-navy px-2.5 py-1 text-xs font-medium text-white hover:bg-datumcraft-ink disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Play size={12} /> Sync now
        </button>
      </header>

      <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-datumcraft-ink">
        <Stat label="Last run"   value={formatRelative(status.lastRunEnded)} />
        <Stat label="Last sync"  value={formatRelative(status.lastSyncedAt)} />
        <Stat label="Rows"       value={status.rowsUpserted.toLocaleString()} />
        <Stat label="Lag"        value={status.lagSeconds == null ? "—" : formatDuration(status.lagSeconds * 1000)} />
      </dl>

      {status.lastError && (
        <p className="mt-2 flex items-start gap-1 rounded border border-red-200 bg-red-50 p-1.5 text-[11px] text-red-700">
          <AlertTriangle size={11} className="mt-0.5 shrink-0" /> {status.lastError}
        </p>
      )}

      <div className="mt-3 border-t border-datumcraft-slate/15 pt-2">
        <div className="flex items-center justify-between">
          <label className="text-[11px] font-medium text-datumcraft-slate">
            <Clock size={11} className="mr-1 inline" />
            Schedule: {schedule?.presetLabel ?? "(custom)"}
          </label>
          <button
            onClick={() => setEditing((v) => !v)}
            className="text-[11px] text-datumcraft-accentDark hover:underline"
          >
            {editing ? "Cancel" : "Edit"}
          </button>
        </div>
        <code className="mt-1 block rounded bg-white px-1.5 py-0.5 text-[10px] text-datumcraft-ink">
          {schedule?.cronExpression ?? "—"}
        </code>

        {editing && (
          <div className="mt-2 space-y-2">
            <div className="flex flex-wrap gap-1">
              {CRON_PRESETS.map((p) => (
                <button
                  key={p.cron}
                  onClick={() => setCustomCron(p.cron)}
                  className={`rounded-full border px-2 py-0.5 text-[10px] ${
                    customCron === p.cron
                      ? "border-datumcraft-accentDark bg-datumcraft-accent/15 text-datumcraft-navy"
                      : "border-datumcraft-slate/30 bg-white text-datumcraft-ink hover:border-datumcraft-accentDark"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <input
              value={customCron}
              onChange={(e) => setCustomCron(e.target.value)}
              placeholder="0 15 */1 * * *"
              className="w-full rounded border border-datumcraft-slate/30 bg-white px-2 py-1 font-mono text-[11px] focus:border-datumcraft-accentDark focus:outline-none"
            />
            <div className="flex items-center justify-between text-[11px]">
              <label className="inline-flex items-center gap-1.5">
                <input
                  type="checkbox"
                  defaultChecked={schedule?.enabled ?? true}
                  onChange={(e) =>
                    save.mutate({ enabled: e.target.checked, cronExpression: customCron })
                  }
                />
                Enabled
              </label>
              <button
                disabled={save.isPending}
                onClick={() => {
                  const preset = CRON_PRESETS.find((p) => p.cron === customCron);
                  save.mutate({
                    cronExpression: customCron,
                    presetLabel: preset ? preset.label : "Custom",
                  });
                }}
                className="rounded-md bg-datumcraft-navy px-2.5 py-1 text-[11px] font-medium text-white hover:bg-datumcraft-ink disabled:opacity-50"
              >
                Save
              </button>
            </div>
          </div>
        )}
      </div>

      {feedback && (
        <p className="mt-2 text-[10px] text-datumcraft-slate" role="status">
          {feedback}
        </p>
      )}
    </article>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <dt className="text-datumcraft-slate">{label}</dt>
      <dd className="font-medium text-datumcraft-ink">{value}</dd>
    </div>
  );
}

function StatusBadge({ kind }: { kind: string }) {
  const palette: Record<string, string> = {
    ok:      "border-green-200 bg-green-50 text-green-700",
    running: "border-amber-200 bg-amber-50 text-amber-700",
    failed:  "border-red-200   bg-red-50   text-red-700",
    idle:    "border-datumcraft-slate/30 bg-white text-datumcraft-slate",
  };
  const icon = kind === "ok" ? (
    <CheckCircle2 size={10} />
  ) : kind === "failed" ? (
    <AlertTriangle size={10} />
  ) : kind === "running" ? (
    <RefreshCw size={10} className="animate-spin" />
  ) : (
    <Clock size={10} />
  );
  return (
    <span
      className={`mt-0.5 inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${
        palette[kind] ?? palette.idle
      }`}
    >
      {icon} {kind}
    </span>
  );
}

function RunHistoryDrawer() {
  const runs = useQuery({
    queryKey: ["metadata-sync", "runs"],
    queryFn: () => fetchSyncRuns(undefined, 50),
  });
  if (runs.isLoading) return <p className="mt-2 text-[11px] text-datumcraft-slate">Loading…</p>;
  const rows = runs.data?.runs ?? [];
  if (rows.length === 0) return <p className="mt-2 text-[11px] text-datumcraft-slate">No runs yet.</p>;
  return (
    <div className="mt-2 overflow-hidden rounded-md border border-datumcraft-slate/20">
      <table className="w-full text-[11px]">
        <thead className="bg-datumcraft-mist/40 text-datumcraft-slate">
          <tr>
            <th className="px-2 py-1 text-left">Source</th>
            <th className="px-2 py-1 text-left">Started</th>
            <th className="px-2 py-1 text-left">Duration</th>
            <th className="px-2 py-1 text-right">Rows</th>
            <th className="px-2 py-1 text-left">Status</th>
            <th className="px-2 py-1 text-left">Triggered by</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-datumcraft-slate/15">
          {rows.map((r) => (
            <tr key={r.runId} className="hover:bg-datumcraft-mist/30">
              <td className="px-2 py-1">{r.sourceName}</td>
              <td className="px-2 py-1">{formatRelative(r.startedAt)}</td>
              <td className="px-2 py-1">{formatDuration(r.durationMs)}</td>
              <td className="px-2 py-1 text-right">
                {(r.rowsUpserted ?? 0).toLocaleString()}
              </td>
              <td className="px-2 py-1">
                {r.status === "failed" && r.errorMessage ? (
                  <span title={r.errorMessage} className="cursor-help">
                    <StatusBadge kind={r.status} />
                  </span>
                ) : (
                  <StatusBadge kind={r.status} />
                )}
              </td>
              <td className="px-2 py-1 text-datumcraft-slate">{r.triggeredBy}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
